import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { logger } from "@/lib/logger";

interface ReviewNotificationCounts {
  vendorApplications: number;
  verifications: number;
  total: number;
}

/**
 * Hook to get counts of items requiring admin review
 * - Pending vendor applications
 * - Pending supplier verifications
 */
export function useAdminReviewNotifications() {
  const { role } = useUserRole();

  const { data: counts, isLoading } = useQuery<ReviewNotificationCounts>({
    queryKey: ['admin-review-notifications'],
    queryFn: async () => {
      // Only fetch if user is admin
      if (role !== 'admin') {
        return { vendorApplications: 0, verifications: 0, total: 0 };
      }

      let vendorApplications = 0;
      let verifications = 0;

      try {
        // Fetch pending vendor applications
        const { count: vendorAppsCount, error: vendorAppsError } = await supabase
          .from('vendor_applications')
          .select('id', { count: 'exact', head: true })
          .in('status', ['pending', 'info_requested']);

        if (vendorAppsError) {
          logger.warn('Error fetching vendor applications count', {
            error: vendorAppsError,
            message: vendorAppsError.message,
            code: vendorAppsError.code,
          });
        } else {
          vendorApplications = vendorAppsCount || 0;
        }
      } catch (error) {
        logger.warn('Exception fetching vendor applications count', { error });
      }

      try {
        // Fetch pending supplier verifications
        // Use proper count query format - when using head: true, select should be minimal
        const { count: verificationsCount, error: verificationsError } = await supabase
          .from('supplier_verifications')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending');

        if (verificationsError) {
          // Only log if it's not a schema/RLS issue (400/404 errors are expected if table/column doesn't exist or RLS blocks)
          const isSchemaError = verificationsError.code === 'PGRST116' || 
                                 verificationsError.code === '42P01' || 
                                 verificationsError.code === '42703' ||
                                 verificationsError.message?.includes('column') ||
                                 verificationsError.message?.includes('does not exist') ||
                                 verificationsError.message?.includes('permission denied');
          
          if (!isSchemaError) {
            logger.warn('Error fetching supplier verifications count', {
              error: verificationsError,
              message: verificationsError.message,
              code: verificationsError.code,
            });
          }
          // Return 0 for verifications on any error
          verifications = 0;
        } else {
          verifications = verificationsCount || 0;
        }
      } catch (error) {
        // Silently handle exceptions - table might not exist or have schema issues
        verifications = 0;
      }

      return {
        vendorApplications,
        verifications,
        total: vendorApplications + verifications,
      };
    },
    enabled: role === 'admin',
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider stale after 10 seconds
    retry: 1, // Only retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
  });

  return {
    counts: counts || { vendorApplications: 0, verifications: 0, total: 0 },
    isLoading,
  };
}

